/*
  Quick add ("Optionen wählen") — opens the shared Heideman bottom sheet with the
  product's own options instead of a full-page modal.

  The product section is fetched from the card's URL and injected as-is: the
  variant picker, engraving fields and bundle logic all live there and must stay
  intact. What a compact "choose your options" step does not need is hidden by
  heideman-quick-add-sheet.css, not stripped out.

  IDs coming from the fetched page are rewritten before injection, because the
  page already contains the main product's section and duplicate IDs would make
  the scripts address the wrong product.
*/
(function () {
  if (window._heidemanQuickAddInit) return;
  window._heidemanQuickAddInit = true;

  var SHEET_ID = 'QuickAddSheet';
  var sheetInstance = null;
  var currentUrl = null;
  var currentPaneIsDesktop = null;

  function getSheet() {
    if (typeof BottomSheetModal === 'undefined') {
      console.warn('[QuickAdd] BottomSheetModal not loaded — cannot open sheet.');
      return null;
    }
    if (!sheetInstance) {
      sheetInstance = new BottomSheetModal(SHEET_ID, {
        closeOnBackdropClick: true,
        closeOnEscape: true,
        enableSwipeToClose: true,
      });
    }
    return sheetInstance;
  }

  /* The sheet component renders its content twice — once for the mobile sheet,
     once for the desktop panel. Only the pane for the current viewport is filled:
     injecting the product form twice would recreate the very id collision this
     sheet had to begin with. */
  function activePane() {
    var modal = document.getElementById(SHEET_ID);
    if (!modal) return null;
    var isDesktop = window.matchMedia('(min-width: 768px)').matches;
    var host = modal.querySelector(isDesktop ? '[data-desktop-content]' : '[data-sheet]');
    return host ? host.querySelector('[data-modal-content]') : null;
  }

  function el(selector) {
    var pane = activePane();
    return pane ? pane.querySelector(selector) : null;
  }

  function setState(state) {
    var loader = el('[data-quick-add-sheet-loader]');
    var error = el('[data-quick-add-sheet-error]');
    var content = el('[data-quick-add-sheet-content]');
    if (loader) loader.style.display = state === 'loading' ? 'flex' : 'none';
    if (error) error.style.display = state === 'error' ? 'block' : 'none';
    if (content) content.style.display = state === 'ready' ? 'block' : 'none';
  }

  /* Clear the pane that is not in use, so a viewport change (rotation, resize)
     can never leave a second copy of the product form behind. */
  function clearInactivePane() {
    var modal = document.getElementById(SHEET_ID);
    if (!modal) return;
    var isDesktop = window.matchMedia('(min-width: 768px)').matches;
    var inactiveHost = modal.querySelector(isDesktop ? '[data-sheet]' : '[data-desktop-content]');
    if (!inactiveHost) return;
    var container = inactiveHost.querySelector('[data-quick-add-sheet-content]');
    if (container) {
      container.innerHTML = '';
      container.style.display = 'none';
    }
  }

  /* Every id/for/aria reference from the fetched section is namespaced, so the
     injected product cannot collide with the main product already on the page.
     The <section> wrapper is kept: its id is what the picker scripts resolve to
     scope themselves, and dropping it sent them looking at the main product. */
  function namespaceIds(section, prefix) {
    var sectionId = section.dataset.section;
    var html = section.outerHTML;
    if (sectionId) {
      html = html.split(sectionId).join(prefix + sectionId);
    }
    return html;
  }

  /* The namespaced section id is not a real Shopify section, so the variant
     scripts must keep fetching section renders under the original id — otherwise
     renderProductInfo() gets nothing back and leaves add-to-cart disabled. */
  function keepOriginalSection(container, originalSectionId) {
    container
      .querySelectorAll('variant-selects, variant-radios, product-info')
      .forEach(function (element) {
        element.dataset.originalSection = originalSectionId;
      });
  }

  function activateScripts(container) {
    container.querySelectorAll('script').forEach(function (oldScript) {
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function removeUnwanted(section) {
    /* Removed rather than hidden: these either fetch on their own, register
       global listeners, or would render a second cart/section controller. */
    var selectors = [
      'pickup-availability',
      'product-modal',
      'modal-dialog',
      'product-recommendations',
      '.shopify-app-block',
      'script[src*="theme-editor"]',
      /* The PDP renders the buy buttons twice — inline and inside the floating
         mobile bar — with the same ids. Keeping both would show two add-to-cart
         buttons and duplicate every id inside the sheet. */
      '.f-floating-chatbubble',
      /* No zoom in the sheet: the lightbox openers and the hover-magnifier sit
         on top of the image strip and swallow the swipe, which made the gallery
         feel unscrollable until every image had finished loading. Tapping an
         image here should do nothing — the options are the point. */
      'script[src*="magnify"]',
      '.product__media-toggle',
      '.product__media-icon',
    ];
    selectors.forEach(function (selector) {
      section.querySelectorAll(selector).forEach(function (node) {
        node.remove();
      });
    });

    /* The picker would otherwise rewrite the address bar of the page the shopper
       is actually on when a variant is chosen inside the sheet. */
    section.querySelectorAll('variant-radios, variant-selects').forEach(function (picker) {
      picker.setAttribute('data-update-url', 'false');
    });

    /* Unwrap the lightbox openers so the images stay visible but stop being
       clickable, and drop the zoom hints the openers carried. */
    section.querySelectorAll('.product__modal-opener').forEach(function (opener) {
      var parent = opener.parentNode;
      if (!parent) return;
      while (opener.firstChild) parent.insertBefore(opener.firstChild, opener);
      parent.removeChild(opener);
    });

    section.querySelectorAll('[data-media-zoom], .product__media-zoom-hover').forEach(function (node) {
      node.classList.remove('product__media-zoom-hover');
      node.removeAttribute('data-media-zoom');
    });
  }

  function open(trigger) {
    var sheet = getSheet();
    if (!sheet) return;

    var url = trigger.getAttribute('data-product-url');
    var title = trigger.getAttribute('data-product-title');
    if (!url) return;

    if (title) sheet.setTitle(title);

    sheet.open();

    var isDesktop = window.matchMedia('(min-width: 768px)').matches;
    clearInactivePane();

    /* Reopening the same product in the same pane keeps the already-rendered
       options, so a shopper who closes the sheet by accident keeps their input.
       After a viewport switch the other pane is empty and must be refilled. */
    if (currentUrl === url && currentPaneIsDesktop === isDesktop) {
      setState('ready');
      return;
    }

    setState('loading');
    var spinner = trigger.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.remove('hidden');
    trigger.setAttribute('aria-disabled', 'true');

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('section[id^="MainProduct-"]');
        if (!section) throw new Error('product section not found');

        removeUnwanted(section);

        var originalSectionId = section.dataset.section;
        var container = el('[data-quick-add-sheet-content]');
        if (!container) throw new Error('sheet container missing');

        var wrapper = document.createElement('div');
        wrapper.className = 'quick-add-sheet__scope';
        wrapper.innerHTML = namespaceIds(section, 'quickadd-');

        container.innerHTML = '';
        container.appendChild(wrapper);
        keepOriginalSection(wrapper, originalSectionId);
        activateScripts(container);

        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();

        currentUrl = url;
        currentPaneIsDesktop = isDesktop;
        setState('ready');
      })
      .catch(function (e) {
        console.error('[QuickAdd]', e);
        currentUrl = null;
        currentPaneIsDesktop = null;
        setState('error');
      })
      .finally(function () {
        if (spinner) spinner.classList.add('hidden');
        trigger.removeAttribute('aria-disabled');
      });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;
    e.preventDefault();
    if (trigger.getAttribute('aria-disabled') === 'true') return;
    open(trigger);
  });

  /* product-form.js calls this after a successful add-to-cart from the sheet. */
  window._heidemanQuickAddClose = function () {
    if (sheetInstance && sheetInstance.isOpen) sheetInstance.close();
  };
})();
